import {IconButton} from '@wandb/components';
import {toast} from '@wandb/weave/common/components/elements/Toast';
import React, {useCallback, useEffect, useRef, useState} from 'react';

import {useWBAgentController} from '../../agentChat/controller';
import type * as RunFeed from '../../util/runfeed';
import {EMPTY_CONFIG} from '../../util/runfeed';
import * as TableCols from '../../util/tablecols';
// FIXME: WBTable should be package-private
import type {WBTableColumn} from '../RunSelector/WBTable/types';
import {entryPointPlaceholders} from '../WBAgent/suggestions/catalog';
import {WBAgentEntryPointIconButton} from '../WBAgent/WBAgentEntryPointButton';
import {useAutoColsRuns} from './hooks/useAutoColsRuns';
import {useRunSelectorAnalyticsLocation} from './RunSelectorAnalyticsLocationContext';
import {type RunSelectorRunsPartialVars} from './types';

interface RunsAutoColsBaseProps {
  onClick(): void;
}

const RunsAutoColsBaseButton = ({onClick}: RunsAutoColsBaseProps) => {
  const analyticsLocation = useRunSelectorAnalyticsLocation();
  const label = 'Show most useful columns';

  return (
    <IconButton
      aria-label={label}
      icon="magic-wand-star"
      variant="secondary"
      onPress={() => {
        onClick();
        window.analytics?.track('Autohide columns button clicked', {
          location: analyticsLocation,
        });
      }}
      tooltip={{children: label}}
    />
  );
};

const RunsAutoColsLoadingButton = (props: RunsAutoColsProps) => {
  const {query, tableSettings, setTableSettings, currentPage, columns} = props;

  const {isLoading, runs} = useAutoColsRuns(query);
  const once = useRef(false);

  useEffect(() => {
    if (!isLoading && tableSettings != null && !once.current) {
      once.current = true;
      const pageSize = tableSettings.pageSize ?? EMPTY_CONFIG.pageSize;
      const i = pageSize * (currentPage - 1);
      const rows = runs.slice(i, i + pageSize);
      setTableSettings(
        TableCols.deriveAutoColumns(tableSettings, rows, columns)
      );
    }
  }, [isLoading, runs, setTableSettings, tableSettings, currentPage, columns]);

  return (
    <IconButton
      icon="magic-wand-star"
      isPending
      aria-label="Show most useful columns"
      variant="secondary"
    />
  );
};

interface RunsAutoColsProps {
  query: RunSelectorRunsPartialVars;
  tableSettings?: RunFeed.Config;
  currentPage: number;
  columns: WBTableColumn[];

  setTableSettings(config: RunFeed.Config): void;
}

export const RunsAutoCols = ({
  query,
  tableSettings,
  setTableSettings,
  currentPage,
  columns,
}: RunsAutoColsProps) => {
  const {isAvailable: isAgentChatAvailable} = useWBAgentController();
  const [loading, setLoading] = useState(false);

  const setTableSettingsWrapped = useCallback(
    (config: RunFeed.Config) => {
      setTableSettings(config);
      setLoading(false);
      toast('Now showing recommended columns.');
    },
    [setTableSettings, setLoading]
  );

  const startLoad = useCallback(() => setLoading(true), [setLoading]);

  if (isAgentChatAvailable) {
    const label = 'Show most useful columns with ARIA';
    return (
      <WBAgentEntryPointIconButton
        aria-label={label}
        data-dd-action-name="Curate runs table columns with agent"
        entryPoint="runs_columns_toolbar"
        placeholder={entryPointPlaceholders.runsColumns}
      />
    );
  }

  return loading ? (
    <RunsAutoColsLoadingButton
      query={query}
      tableSettings={tableSettings}
      setTableSettings={setTableSettingsWrapped}
      currentPage={currentPage}
      columns={columns}
    />
  ) : (
    <RunsAutoColsBaseButton onClick={startLoad} />
  );
};
